import React, { useEffect, useRef, useState } from 'react';
import { Search, Check, ChevronDown, X } from 'lucide-react';

/**
 * Reusable searchable dropdown for selecting a driver by name.
 * Props:
 *  - drivers: [{ user_id, name, status, ... }]
 *  - value: string (selected user_id)
 *  - onChange: (user_id, driverObj) => void
 *  - placeholder
 *  - testid
 *  - showInactive (default false) — when false only active drivers are shown
 */
const SearchableDriverSelect = ({
  drivers = [],
  value,
  onChange,
  placeholder = 'Search a driver…',
  testid = 'driver-select',
  showInactive = false,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const pool = (drivers || []).filter((d) => showInactive || d.status === 'active');
  const selected = pool.find((d) => d.user_id === value) || drivers.find((d) => d.user_id === value);

  const filtered = pool.filter((d) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      (d.name || '').toLowerCase().includes(q) ||
      (d.email || '').toLowerCase().includes(q) ||
      (d.phone || '').toLowerCase().includes(q)
    );
  });

  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  useEffect(() => {
    if (open) {
      // Auto-focus the search input when opened
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  const handlePick = (d) => {
    onChange?.(d.user_id, d);
    setOpen(false);
    setQuery('');
  };

  const clearSelection = (e) => {
    e.stopPropagation();
    onChange?.('', null);
  };

  return (
    <div ref={wrapperRef} className="relative w-full" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testid}-trigger`}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-slate-300 rounded-md bg-white text-left text-sm hover:border-slate-400 focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
      >
        <span className={`truncate ${selected ? 'text-slate-900' : 'text-slate-400'}`}>
          {selected ? selected.name || selected.email : placeholder}
        </span>
        <span className="flex items-center gap-1 flex-shrink-0">
          {selected && (
            <X
              onClick={clearSelection}
              data-testid={`${testid}-clear`}
              className="w-4 h-4 text-slate-400 hover:text-slate-700"
            />
          )}
          <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 left-0 right-0 bg-white rounded-md shadow-lg border border-slate-200 max-h-72 overflow-hidden flex flex-col"
          data-testid={`${testid}-panel`}
        >
          <div className="px-3 py-2 border-b border-slate-100 bg-slate-50">
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4 text-slate-400" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type a name…"
                data-testid={`${testid}-search`}
                className="flex-1 bg-transparent border-none outline-none text-sm placeholder:text-slate-400"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              />
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-4 text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                No drivers match.
              </p>
            ) : (
              filtered.map((d) => {
                const isSel = d.user_id === value;
                return (
                  <button
                    key={d.user_id}
                    type="button"
                    onClick={() => handlePick(d)}
                    data-testid={`${testid}-option-${d.user_id}`}
                    className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 hover:bg-slate-50 ${isSel ? 'bg-[#002FA7]/5' : ''}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        {d.name || d.email}
                      </p>
                      <p className="text-xs text-slate-500 truncate" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        {d.email}{d.status && d.status !== 'active' ? ` • ${d.status}` : ''}
                      </p>
                    </div>
                    {isSel && <Check className="w-4 h-4 text-[#002FA7] flex-shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableDriverSelect;
